import { useEffect, useId, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  PERMISSIONS,
  apSelfAssessmentReviewSchema,
  vendorFiscalFxReviewSchema,
  apSelfAssessmentCaseSchema,
  apSelfAssessmentIsNonRecipientCase,
  calculateVendorInvoiceAccountingAllocation,
  vendorInvoiceAccountingSplitSaveSchema,
  vendorInvoiceAccountingSplitWriteSchema,
  type VendorInvoiceAccountingPreview,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { apiDownload, saveBlob } from '@/lib/api-client';
import { usePermissions } from '@/features/auth';
import { InlineHelp } from '@/features/help';
import { FinancialFormProtection } from './financial-form-protection';
import { formatLedgerMoney } from '../lib/ledger-format';
import {
  useApproveReviewedVendorInvoice,
  useSaveVendorInvoiceAccountingSplits,
  useVendorInvoiceAccountingPreview,
} from '../api/use-vendor-invoice-accounting';

const decimalInput = z.string().transform((value) => value.trim().replace(',', '.'));
const builderSchema = z
  .object({
    version: z.number().int().positive(),
    fiscalFx: vendorFiscalFxReviewSchema
      .innerType()
      .extend({
        euroTaxBuckets: z.array(
          vendorFiscalFxReviewSchema
            .innerType()
            .shape.euroTaxBuckets.removeDefault()
            .element.extend({ statedTaxAmount: decimalInput, legalTaxAmount: decimalInput }),
        ),
      })
      .nullish(),
    selfAssessment: apSelfAssessmentReviewSchema
      .innerType()
      .extend({
        fiscalRateId: z.string().optional(),
        case: z.string(),
        eligibilityConfirmed: z.boolean(),
        noSpecialTimingConfirmed: z.boolean(),
      })
      .nullish(),
    splits: z.array(
      vendorInvoiceAccountingSplitWriteSchema.extend({
        description: vendorInvoiceAccountingSplitWriteSchema.shape.description.removeDefault(),
        netAmount: decimalInput,
        taxRate: decimalInput,
        inputTaxDeductionPercent: decimalInput,
        costCenterId: z
          .string()
          .nullish()
          .transform((value) => value || null),
        costObjectId: z
          .string()
          .nullish()
          .transform((value) => value || null),
      }),
    ),
  })
  .pipe(vendorInvoiceAccountingSplitSaveSchema);
type BuilderInput = z.input<typeof builderSchema>;
function valuesFor(preview: VendorInvoiceAccountingPreview): BuilderInput {
  return {
    version: preview.version,
    fiscalFx:
      preview.fiscalFxContext?.review ??
      (preview.fiscalFxContext?.required
        ? {
            taxCurrency: 'DOCUMENT',
            sourceHash: preview.fiscalFxContext.sourceHash,
            evidenceText: '',
            euroTaxBuckets: [],
          }
        : null),
    selfAssessment: preview.selfAssessmentContext?.review ?? null,
    splits: preview.splits.length
      ? preview.splits
      : preview.effectiveSplits.length
        ? preview.effectiveSplits
        : preview.sourceBuckets.map((bucket, index) => ({
            position: index + 1,
            accountId: '',
            netAmount: bucket.netAmount,
            taxRate: bucket.taxRate,
            inputTaxDeductionPercent: '100',
            costCenterId: null,
            costObjectId: null,
            description: '',
          })),
  };
}

export function VendorInvoiceAccountingDialog({
  invoiceId,
  onClose,
}: {
  invoiceId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(),
    { can } = usePermissions();
  const query = useVendorInvoiceAccountingPreview(invoiceId),
    save = useSaveVendorInvoiceAccountingSplits(invoiceId),
    approve = useApproveReviewedVendorInvoice(invoiceId);
  const prefix = useId();
  const [review, setReview] = useState<VendorInvoiceAccountingPreview | null>(null);
  const [editing, setEditing] = useState(false),
    [error, setError] = useState(''),
    [reason, setReason] = useState(''),
    [checked, setChecked] = useState(false),
    [completed, setCompleted] = useState(false);
  const retryKey = useRef(crypto.randomUUID());
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const form = useForm<BuilderInput>({
    resolver: zodResolver(builderSchema),
    defaultValues: { version: 1, splits: [] },
  });
  const rows = useFieldArray({ control: form.control, name: 'splits' });
  const inputs = form.watch();
  useEffect(() => {
    const current = form.getValues('selfAssessment');
    if (!current) return;
    if (apSelfAssessmentIsNonRecipientCase(current.case) && current.fiscalRateId !== undefined) {
      const { fiscalRateId: _fiscalRateId, ...nonRecipientReview } = current;
      form.setValue('selfAssessment', nonRecipientReview, { shouldDirty: true });
    }
    const allocations = apSelfAssessmentIsNonRecipientCase(current.case)
      ? []
      : form
          .getValues('splits')
          .filter((part) => /^0(?:[.,]0+)?$/.test(part.taxRate))
          .map(
            (part) =>
              current.allocations.find((value) => value.position === part.position) ?? {
                position: part.position,
                rate: '19' as const,
                inputTaxAccountId: '',
                outputTaxAccountId: '',
              },
          );
    if (JSON.stringify(allocations) !== JSON.stringify(current.allocations))
      form.setValue('selfAssessment.allocations', allocations, { shouldDirty: true });
  }, [inputs.splits, inputs.selfAssessment?.case, form]);
  const dirty = !completed && (form.formState.isDirty || reason.trim().length > 0);
  const pending = save.isPending || approve.isPending;
  const stale =
    review !== null && query.data !== undefined && review.previewHash !== query.data.previewHash;
  const localInput = builderSchema.safeParse(inputs);
  const local =
    review?.exchangeRate && localInput.success && review.sourceBuckets.length
      ? calculateVendorInvoiceAccountingAllocation({
          buckets: review.sourceBuckets,
          splits: localInput.data.splits,
          exchangeRate: review.exchangeRate,
        })
      : null;
  const editable = can(PERMISSIONS.LEDGER_WRITE) && review !== null && !review.readOnly;
  const mayApprove =
    can(PERMISSIONS.PURCHASE_INVOICE_APPROVE) && review !== null && !review.readOnly;
  useEffect(() => {
    if (!query.data || review || query.isFetching) return;
    setReview(query.data);
    form.reset(valuesFor(query.data));
  }, [query.data, query.isFetching, review, form]);
  useEffect(() => {
    if (completed) onClose();
  }, [completed, onClose]);
  const accept = (next: VendorInvoiceAccountingPreview, retainInputs: boolean) => {
    setReview(next);
    setChecked(false);
    retryKey.current = crypto.randomUUID();
    if (retainInputs) {
      form.setValue('version', next.version);
      if (form.getValues('fiscalFx') && next.fiscalFxContext)
        form.setValue('fiscalFx.sourceHash', next.fiscalFxContext.sourceHash);
      if (form.getValues('selfAssessment') && next.selfAssessmentContext)
        form.setValue(
          'selfAssessment.sourceFingerprint',
          next.selfAssessmentContext.sourceFingerprint,
        );
    } else form.reset(valuesFor(next));
  };
  async function refresh() {
    setError('');
    try {
      const result = await query.refetch();
      if (!result.data || result.error) throw result.error ?? new Error('ledger.errors.validation');
      accept(result.data, form.formState.isDirty);
    } catch (value) {
      setError(value instanceof Error ? t(value.message) : t('ledger.errors.validation'));
    }
  }
  const saveSplits = form.handleSubmit(
    async (input) => {
      const body = builderSchema.parse(input);
      if (!editable || pending || stale) return;
      const calculation = calculateVendorInvoiceAccountingAllocation({
        buckets: review.sourceBuckets,
        splits: body.splits,
        exchangeRate: review.exchangeRate ?? '1',
      });
      if (!calculation.eligible) {
        setError(
          t(`ledger.vendorAllocation.findings.${calculation.findings[0] ?? 'invalidInput'}`),
        );
        form.setFocus('splits.0.netAmount');
        return;
      }
      if (
        review.currency !== review.baseCurrency &&
        body.selfAssessment &&
        !apSelfAssessmentIsNonRecipientCase(body.selfAssessment.case) &&
        !body.selfAssessment.fiscalRateId
      ) {
        setError(t('fiscalFx.errors.missing'));
        form.setError('selfAssessment.fiscalRateId', {
          type: 'required',
          message: 'fiscalFx.errors.missing',
        });
        form.setFocus('selfAssessment.fiscalRateId');
        return;
      }
      setError('');
      try {
        await save.mutateAsync(body);
        form.reset(body);
        setEditing(false);
        setChecked(false);
        const result = await query.refetch();
        if (!result.data || result.error)
          throw result.error ?? new Error('ledger.errors.validation');
        accept(result.data, false);
        toast.success(t('ledger.vendorAllocation.saved'));
      } catch (value) {
        setError(value instanceof Error ? t(value.message) : t('ledger.errors.validation'));
      }
    },
    () => setError(t('ledger.vendorAllocation.findings.invalidInput')),
  );
  async function confirm() {
    if (
      !mayApprove ||
      review.matchingRequired ||
      !review.eligible ||
      !checked ||
      editing ||
      stale ||
      pending
    )
      return;
    setError('');
    try {
      await approve.mutateAsync({
        version: review.version,
        previewHash: review.previewHash,
        idempotencyKey: retryKey.current,
        overrideReason: reason.trim() || null,
      });
      toast.success(t('procurement.match.approved'));
      setCompleted(true);
    } catch (value) {
      setError(value instanceof Error ? t(value.message) : t('ledger.errors.validation'));
    }
  }
  return (
    <FinancialFormProtection dirty={dirty} pending={pending} onClose={onClose}>
      {(close) => (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <DialogContent
            closeLabel={t('common.close')}
            className="max-h-[92dvh] overflow-y-auto sm:max-w-4xl [&_button]:min-h-11"
          >
            <DialogTitle>{t('ledger.vendorAllocation.title')}</DialogTitle>
            <DialogDescription>{t('ledger.vendorAllocation.hint')}</DialogDescription>
            <InlineHelp articleId="finance.vendor-invoice-allocation" label={t('ledger.help')} />
            {query.isPending || (!review && !query.isError) ? (
              <Skeleton className="h-56" />
            ) : !review ? (
              <div role="alert">
                <p>{t('ledger.errors.load')}</p>
                <Button type="button" onClick={() => void refresh()}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap justify-between gap-3">
                  <p className="font-medium">{review.invoiceNumber}</p>
                  <p className="tabular-nums">
                    {formatLedgerMoney(review.grossAmount, review.currency, i18n.language)}
                  </p>
                </div>
                <dl className="grid gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-muted-foreground">{t('ledger.bookingDate')}</dt>
                    <dd>{review.bookingDate ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {t('ledger.vendorCorrection.taxDate')}
                    </dt>
                    <dd>{review.taxDate ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('ledger.vendorAllocation.rate')}</dt>
                    <dd>
                      {review.currency} → {review.baseCurrency}: {review.exchangeRate ?? '—'}
                    </dd>
                  </div>
                  {review.currency !== review.baseCurrency ? (
                    <div className="sm:col-span-3">
                      <dt className="text-muted-foreground">{t('ledgerPayments.rateSource')}</dt>
                      <dd className="break-words">
                        {review.exchangeRateSource ?? t('common.none')}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {review.selfAssessmentContext?.proof ? (
                  <section
                    className="space-y-2 rounded-md border p-3"
                    aria-label={t('ledger.selfAssessment.title')}
                  >
                    <h3 className="font-medium">{t('ledger.selfAssessment.title')}</h3>
                    <p>
                      {t(
                        `ledger.selfAssessment.cases.${review.selfAssessmentContext.proof.review.case}`,
                      )}
                    </p>
                    <dl className="grid gap-2 sm:grid-cols-2">
                      {(
                        [
                          'originalTaxAmount',
                          'legalTaxAmount',
                          'deductibleTaxAmount',
                          'payableAmount',
                          'costAmount',
                        ] as const
                      ).map((field, index) => (
                        <div key={field}>
                          <dt className="text-muted-foreground">
                            {t(
                              `ledger.selfAssessment.${['original', 'legal', 'deductible', 'payable', 'cost'][index]}`,
                            )}
                          </dt>
                          <dd>
                            {formatLedgerMoney(
                              review.selfAssessmentContext?.proof?.[field] ?? '0',
                              ['legalTaxAmount', 'deductibleTaxAmount', 'costAmount'].includes(
                                field,
                              )
                                ? review.baseCurrency
                                : review.currency,
                              i18n.language,
                            )}
                          </dd>
                        </div>
                      ))}
                      <div>
                        <dt className="text-muted-foreground">
                          {t('ledger.selfAssessment.residual')}
                        </dt>
                        <dd>
                          {formatLedgerMoney(
                            review.selfAssessmentContext.residualPayableAmount ??
                              review.selfAssessmentContext.proof.payableAmount,
                            review.currency,
                            i18n.language,
                          )}
                        </dd>
                      </div>
                    </dl>
                    <p className="text-sm">
                      {review.selfAssessmentContext.proof.legalBasis} ·{' '}
                      {review.selfAssessmentContext.proof.taxDate}
                    </p>
                    {review.selfAssessmentContext.proof.fiscalFx ? (
                      <p className="text-sm text-muted-foreground">
                        {t('fiscalFx.title')}:{' '}
                        {review.selfAssessmentContext.proof.fiscalFx.rate.quote} ·{' '}
                        {t(
                          `fiscalFx.${review.selfAssessmentContext.proof.fiscalFx.rate.quoteDirection}`,
                        )}{' '}
                        · {review.selfAssessmentContext.proof.fiscalFx.taxDate}
                      </p>
                    ) : null}
                  </section>
                ) : null}
                {review.advanceReview ? (
                  <section
                    className="space-y-2 rounded-md border p-3"
                    aria-label={t('documentLock.advanceReview')}
                  >
                    <h3 className="font-medium">{t('documentLock.advanceReview')}</h3>
                    {review.advanceReview.sources.map((source) => (
                      <div key={`${source.number}-${source.date}`}>
                        <p>
                          {source.number} · {source.date} · {t('documentLock.advanceGross')}:{' '}
                          {formatLedgerMoney(source.gross, review.currency, i18n.language)}
                        </p>
                        {source.buckets.map((bucket) => (
                          <p key={bucket.rate}>
                            {bucket.rate}% · {t('documentLock.advanceNet')}:{' '}
                            {formatLedgerMoney(bucket.net, review.currency, i18n.language)} ·{' '}
                            {t('documentLock.advanceTax')}:{' '}
                            {formatLedgerMoney(bucket.tax, review.currency, i18n.language)} ·{' '}
                            {t('documentLock.advanceDeductibleTax')}:{' '}
                            {formatLedgerMoney(
                              bucket.deductibleTax,
                              review.currency,
                              i18n.language,
                            )}
                          </p>
                        ))}
                      </div>
                    ))}
                    <p>
                      {t('documentLock.advanceDue')}:{' '}
                      {formatLedgerMoney(
                        review.advanceReview.residualDue,
                        review.currency,
                        i18n.language,
                      )}
                    </p>
                    {review.advanceReview.declaredPrepaid === null ? (
                      <p>{t('documentLock.advanceOriginalReview')}</p>
                    ) : (
                      <p>
                        {t('documentLock.advanceDeclaredPrepaid')}:{' '}
                        {formatLedgerMoney(
                          review.advanceReview.declaredPrepaid,
                          review.currency,
                          i18n.language,
                        )}{' '}
                        · {t('documentLock.advanceDeclaredDue')}:{' '}
                        {review.advanceReview.declaredDue === null
                          ? '—'
                          : formatLedgerMoney(
                              review.advanceReview.declaredDue,
                              review.currency,
                              i18n.language,
                            )}
                      </p>
                    )}
                  </section>
                ) : null}
                {review.journalId ? (
                  <Link
                    className="text-sm text-primary underline"
                    to={`/finance/journal?journalId=${encodeURIComponent(review.journalId)}`}
                  >
                    {t('ledgerPayments.journal')}
                  </Link>
                ) : null}
                {review.original ? (
                  <div className="space-y-1 rounded-md border border-border p-3 text-sm">
                    <p className="font-medium">
                      {t('procurement.originals.reviewed')}
                      {review.original.revision
                        ? ` · ${t('procurement.originals.version', { number: review.original.revision })}`
                        : ''}
                    </p>
                    <p>{review.original.fileName}</p>
                    <p className="break-all font-mono text-xs text-muted-foreground">
                      {review.original.fileHash}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={downloadingOriginal}
                      onClick={() => {
                        const original = review.original;
                        if (!original) return;
                        setError('');
                        setDownloadingOriginal(true);
                        const suffix = original.id
                          ? `/originals/${encodeURIComponent(original.id)}/file`
                          : '/document';
                        void apiDownload(
                          `/api/v1/procurement/vendor-invoices/${encodeURIComponent(invoiceId)}${suffix}?fileHash=${original.fileHash}`,
                          original.fileName,
                        )
                          .then(saveBlob)
                          .catch((value: unknown) =>
                            setError(
                              value instanceof Error
                                ? t(value.message)
                                : t('procurement.match.documentError'),
                            ),
                          )
                          .finally(() => setDownloadingOriginal(false));
                      }}
                    >
                      {t('procurement.originals.reviewedDownload')}
                    </Button>
                  </div>
                ) : null}
                {review.noPostingRequired ? (
                  <p className="text-sm">{t('ledger.vendorAllocation.zero')}</p>
                ) : null}
                {review.issues.length ? (
                  <ul className="space-y-1 text-sm">
                    {review.issues.map((issue, index) => (
                      <li
                        key={`${issue.code}-${index}`}
                        className={
                          issue.severity === 'ERROR' && !review.readOnly
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                        }
                      >
                        {t(issue.code)}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {review.matchingRequired ? (
                  <p className="text-sm text-muted-foreground">
                    {t('ledger.vendorAllocation.matchRequired')}
                  </p>
                ) : null}
                {stale ? (
                  <p className="text-sm text-destructive">{t('ledger.errors.stale')}</p>
                ) : null}
                {error ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive p-3 text-sm text-destructive"
                  >
                    {error}
                  </p>
                ) : null}
                {!editing && review.fiscalFxContext?.proof ? (
                  <section
                    className="space-y-3 rounded-md border p-3"
                    aria-label={t('fiscalFx.vendorTitle')}
                  >
                    <h3 className="font-medium">{t('fiscalFx.vendorTitle')}</h3>
                    <dl className="grid gap-2 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">{t('fiscalFx.vendorCurrency')}</dt>
                        <dd>
                          {t(
                            review.fiscalFxContext.proof.review.taxCurrency === 'EUR'
                              ? 'fiscalFx.vendorEuro'
                              : 'fiscalFx.vendorDocument',
                          )}{' '}
                          · {review.fiscalFxContext.proof.sourceCurrency}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">{t('fiscalFx.reviewRateDate')}</dt>
                        <dd>{review.fiscalFxContext.proof.rateTaxDate}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">
                          {t('fiscalFx.reviewReportingDate')}
                        </dt>
                        <dd>{review.fiscalFxContext.proof.reportingTaxDate}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">{t('fiscalFx.vendorEvidence')}</dt>
                        <dd className="whitespace-pre-wrap break-words">
                          {review.fiscalFxContext.proof.review.evidenceText}
                        </dd>
                      </div>
                      {review.fiscalFxContext.proof.fiscalFx ? (
                        <div>
                          <dt className="text-muted-foreground">{t('fiscalFx.quote')}</dt>
                          <dd>
                            {review.fiscalFxContext.proof.fiscalFx.rate.quote} ·{' '}
                            {review.fiscalFxContext.proof.fiscalFx.rate.quoteDirection ===
                            'EUR_PER_FOREIGN'
                              ? `EUR / ${review.fiscalFxContext.proof.sourceCurrency}`
                              : `${review.fiscalFxContext.proof.sourceCurrency} / EUR`}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                    {review.fiscalFxContext.proof.rows.map((row) => (
                      <dl
                        key={row.position}
                        className="grid gap-2 border-t pt-2 text-sm sm:grid-cols-3"
                      >
                        <div>
                          <dt className="text-muted-foreground">
                            {t('ledger.vendorAllocation.row', { number: row.position })}
                          </dt>
                          <dd>{row.taxRate}%</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">{t('fiscalFx.vendorLegal')}</dt>
                          <dd>{formatLedgerMoney(row.legalTax, 'EUR', i18n.language)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">
                            {t('ledger.selfAssessment.deductible')}
                          </dt>
                          <dd>{formatLedgerMoney(row.deductibleTax, 'EUR', i18n.language)}</dd>
                        </div>
                      </dl>
                    ))}
                  </section>
                ) : null}
                {editing ? (
                  <form
                    className="space-y-3"
                    onSubmit={(event) => void saveSplits(event)}
                    onKeyDown={(event) => {
                      if ((event.ctrlKey || event.metaKey) && ['s', 'Enter'].includes(event.key)) {
                        event.preventDefault();
                        event.currentTarget.requestSubmit();
                      }
                    }}
                  >
                    <p className="text-sm text-muted-foreground">
                      {t('ledger.vendorAllocation.amountHint', { currency: review.currency })}
                    </p>
                    {review.fiscalFxContext?.required && inputs.fiscalFx ? (
                      <fieldset className="space-y-3 rounded-md border p-3">
                        <legend className="px-1 font-medium">{t('fiscalFx.vendorTitle')}</legend>
                        <p className="text-sm text-muted-foreground">{t('fiscalFx.vendorHint')}</p>
                        <Label htmlFor={`${prefix}-fiscal-currency`}>
                          {t('fiscalFx.vendorCurrency')}
                        </Label>
                        <select
                          id={`${prefix}-fiscal-currency`}
                          className="h-11 w-full rounded-md border bg-background px-2"
                          {...form.register('fiscalFx.taxCurrency', {
                            onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
                              form.setValue(
                                'fiscalFx.euroTaxBuckets',
                                event.target.value === 'EUR'
                                  ? review.sourceBuckets.map((bucket) => ({
                                      taxRate: bucket.taxRate,
                                      statedTaxAmount: '',
                                      legalTaxAmount: '',
                                    }))
                                  : [],
                                { shouldDirty: true },
                              );
                            },
                          })}
                        >
                          <option value="DOCUMENT">{t('fiscalFx.vendorDocument')}</option>
                          <option value="EUR">{t('fiscalFx.vendorEuro')}</option>
                        </select>
                        <Label htmlFor={`${prefix}-fiscal-evidence`}>
                          {t('fiscalFx.vendorEvidence')}
                        </Label>
                        <textarea
                          id={`${prefix}-fiscal-evidence`}
                          className="min-h-24 w-full rounded-md border bg-background p-2"
                          {...form.register('fiscalFx.evidenceText')}
                          aria-invalid={!!form.formState.errors.fiscalFx?.evidenceText}
                        />
                        {inputs.fiscalFx.euroTaxBuckets.map((bucket, index) => (
                          <div key={bucket.taxRate} className="grid gap-2 sm:grid-cols-2">
                            <div>
                              <Label htmlFor={`${prefix}-fiscal-stated-${index}`}>
                                {t('fiscalFx.vendorStated')} ({bucket.taxRate}%)
                              </Label>
                              <Input
                                id={`${prefix}-fiscal-stated-${index}`}
                                inputMode="decimal"
                                {...form.register(
                                  `fiscalFx.euroTaxBuckets.${index}.statedTaxAmount`,
                                )}
                              />
                            </div>
                            <div>
                              <Label htmlFor={`${prefix}-fiscal-legal-${index}`}>
                                {t('fiscalFx.vendorLegal')} ({bucket.taxRate}%)
                              </Label>
                              <Input
                                id={`${prefix}-fiscal-legal-${index}`}
                                inputMode="decimal"
                                {...form.register(
                                  `fiscalFx.euroTaxBuckets.${index}.legalTaxAmount`,
                                )}
                              />
                            </div>
                          </div>
                        ))}
                        <Link to="/finance/vat-rates" className="text-sm underline">
                          {t('fiscalFx.vendorRate')}
                        </Link>
                      </fieldset>
                    ) : null}
                    <fieldset className="space-y-3 rounded-md border p-3">
                      <legend className="px-1 font-medium">
                        {t('ledger.selfAssessment.title')}
                      </legend>
                      <Label
                        className="flex min-h-11 items-center gap-2"
                        htmlFor={`${prefix}-self-tax`}
                      >
                        <input
                          id={`${prefix}-self-tax`}
                          type="checkbox"
                          checked={!!inputs.selfAssessment}
                          onChange={(event) =>
                            form.setValue(
                              'selfAssessment',
                              event.target.checked
                                ? {
                                    ...(review.grossAmount.startsWith('-') &&
                                    review.currency !== review.baseCurrency &&
                                    review.selfAssessmentContext?.fiscalRates?.length === 1
                                      ? {
                                          fiscalRateId:
                                            review.selfAssessmentContext.fiscalRates[0]?.id,
                                        }
                                      : {}),
                                    case: review.selfAssessmentContext?.recommendation ?? '',
                                    sourceFingerprint:
                                      review.selfAssessmentContext?.sourceFingerprint ?? '',
                                    reason: '',
                                    eligibilityConfirmed: false,
                                    noSpecialTimingConfirmed: false,
                                    allocations: form
                                      .getValues('splits')
                                      .filter((part) => ['0', '0.000000'].includes(part.taxRate))
                                      .map((part) => ({
                                        position: part.position,
                                        rate: '19',
                                        inputTaxAccountId: '',
                                        outputTaxAccountId: '',
                                      })),
                                  }
                                : null,
                              { shouldDirty: true },
                            )
                          }
                        />
                        {t('ledger.selfAssessment.enable')}
                      </Label>
                      {inputs.selfAssessment ? (
                        <>
                          <p className="text-sm text-muted-foreground">
                            {t('ledger.selfAssessment.hint')}
                          </p>
                          <InlineHelp
                            articleId="finance.ap-self-assessment"
                            label={t('ledger.help')}
                          />
                          <Label htmlFor={`${prefix}-self-case`}>
                            {t('ledger.selfAssessment.case')}
                          </Label>
                          <select
                            id={`${prefix}-self-case`}
                            className="h-11 w-full rounded-md border bg-background px-2"
                            {...form.register('selfAssessment.case')}
                            aria-invalid={!!form.formState.errors.selfAssessment?.case}
                          >
                            <option value="">{t('common.select')}</option>
                            {apSelfAssessmentCaseSchema.options.map((value) => (
                              <option key={value} value={value}>
                                {t(`ledger.selfAssessment.cases.${value}`)}
                              </option>
                            ))}
                          </select>
                          {review.currency !== review.baseCurrency &&
                          !apSelfAssessmentIsNonRecipientCase(inputs.selfAssessment.case) ? (
                            <>
                              <Label htmlFor={`${prefix}-self-fiscal-rate`}>
                                {t('fiscalFx.title')}
                              </Label>
                              <select
                                id={`${prefix}-self-fiscal-rate`}
                                className="h-11 w-full rounded-md border bg-background px-2"
                                {...form.register('selfAssessment.fiscalRateId')}
                                aria-invalid={!!form.formState.errors.selfAssessment?.fiscalRateId}
                              >
                                <option value="">{t('common.select')}</option>
                                {review.selfAssessmentContext?.fiscalRates?.map((rate) => (
                                  <option key={rate.id} value={rate.id}>
                                    {rate.sourceCurrency} · {rate.validFrom}–{rate.validTo} ·{' '}
                                    {rate.quote} · {t(`fiscalFx.${rate.quoteDirection}`)}
                                  </option>
                                ))}
                              </select>
                              <Link className="underline" to="/finance/vat-rates">
                                {t('fiscalFx.title')}
                              </Link>
                            </>
                          ) : null}
                          <Label htmlFor={`${prefix}-self-reason`}>
                            {t('ledger.selfAssessment.reason')}
                          </Label>
                          <Input
                            id={`${prefix}-self-reason`}
                            {...form.register('selfAssessment.reason')}
                            maxLength={2000}
                            aria-invalid={!!form.formState.errors.selfAssessment?.reason}
                          />
                          {(['eligibilityConfirmed', 'noSpecialTimingConfirmed'] as const).map(
                            (field, index) => (
                              <Label
                                key={field}
                                className="flex min-h-11 items-center gap-2"
                                htmlFor={`${prefix}-${field}`}
                              >
                                <input
                                  id={`${prefix}-${field}`}
                                  type="checkbox"
                                  {...form.register(`selfAssessment.${field}`)}
                                />
                                {t(
                                  `ledger.selfAssessment.${index === 0 ? 'eligibility' : 'timing'}`,
                                )}
                              </Label>
                            ),
                          )}
                          {inputs.selfAssessment.allocations.map((part, index) => (
                            <fieldset
                              key={part.position}
                              className="grid gap-2 rounded-md border p-3 sm:grid-cols-3"
                            >
                              <legend>
                                {t('ledger.vendorAllocation.row', { number: part.position })}
                              </legend>
                              {review.grossAmount.startsWith('-') ? (
                                <div>
                                  <Label htmlFor={`${prefix}-self-source-${index}`}>
                                    {t('ledger.selfAssessment.sourcePosition')}
                                  </Label>
                                  <select
                                    id={`${prefix}-self-source-${index}`}
                                    className="h-11 w-full rounded-md border bg-background px-2"
                                    value={part.sourcePosition ?? ''}
                                    onChange={(event) =>
                                      form.setValue(
                                        `selfAssessment.allocations.${index}.sourcePosition`,
                                        event.target.value ? Number(event.target.value) : undefined,
                                        { shouldDirty: true },
                                      )
                                    }
                                  >
                                    <option value="">{t('common.select')}</option>
                                    {review.selfAssessmentContext?.sourceAllocations?.map(
                                      (source) => (
                                        <option key={source.position} value={source.position}>
                                          {source.position} · {source.accountNumber}{' '}
                                          {source.accountName} · {source.rate}% ·{' '}
                                          {formatLedgerMoney(
                                            source.netAmount,
                                            review.currency,
                                            i18n.language,
                                          )}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                </div>
                              ) : null}
                              <div>
                                <Label htmlFor={`${prefix}-self-rate-${index}`}>
                                  {t('ledger.selfAssessment.rate')}
                                </Label>
                                <select
                                  id={`${prefix}-self-rate-${index}`}
                                  className="h-11 w-full rounded-md border bg-background px-2"
                                  {...form.register(`selfAssessment.allocations.${index}.rate`)}
                                >
                                  {['19', '7', '0'].map((value) => (
                                    <option key={value} value={value}>
                                      {value}%
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {(['inputTaxAccountId', 'outputTaxAccountId'] as const).map(
                                (field, accountIndex) => (
                                  <div key={field}>
                                    <Label htmlFor={`${prefix}-self-${field}-${index}`}>
                                      {t(
                                        `ledger.selfAssessment.${accountIndex === 0 ? 'inputAccount' : 'outputAccount'}`,
                                      )}
                                    </Label>
                                    <select
                                      id={`${prefix}-self-${field}-${index}`}
                                      className="h-11 w-full rounded-md border bg-background px-2"
                                      {...form.register(
                                        `selfAssessment.allocations.${index}.${field}`,
                                      )}
                                    >
                                      <option value="">{t('common.select')}</option>
                                      {review.selfAssessmentContext?.taxAccounts
                                        .filter(
                                          (account) =>
                                            account.role ===
                                            (accountIndex === 0 ? 'INPUT_TAX' : 'OUTPUT_TAX'),
                                        )
                                        .map((account) => (
                                          <option key={account.id} value={account.id}>
                                            {account.number} · {account.name}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                ),
                              )}
                            </fieldset>
                          ))}
                        </>
                      ) : null}
                    </fieldset>
                    {rows.fields.map((row, index) => (
                      <fieldset
                        key={row.id}
                        className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-3"
                      >
                        <legend className="px-1 text-sm font-medium">
                          {t('ledger.vendorAllocation.row', { number: index + 1 })}
                        </legend>
                        <div>
                          <Label htmlFor={`${prefix}-account-${index}`}>
                            {t('ledger.vendorAllocation.account', { number: index + 1 })}
                          </Label>
                          <select
                            id={`${prefix}-account-${index}`}
                            {...form.register(`splits.${index}.accountId`)}
                            aria-invalid={!!form.formState.errors.splits?.[index]?.accountId}
                            className="mt-1 h-11 w-full rounded-md border border-input bg-background px-2 text-sm"
                          >
                            <option value="">{t('common.select')}</option>
                            {review.availableAccounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.number} · {account.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {(['netAmount', 'taxRate', 'inputTaxDeductionPercent'] as const).map(
                          (field) => (
                            <div key={field}>
                              <Label htmlFor={`${prefix}-${field}-${index}`}>
                                {t(`ledger.vendorAllocation.${field}`, { number: index + 1 })}
                              </Label>
                              <Input
                                id={`${prefix}-${field}-${index}`}
                                inputMode="decimal"
                                {...form.register(`splits.${index}.${field}`)}
                                aria-invalid={!!form.formState.errors.splits?.[index]?.[field]}
                                className="mt-1 min-h-11"
                              />
                              {form.formState.errors.splits?.[index]?.[field] ? (
                                <p className="text-xs text-destructive">
                                  {t('ledger.vendorAllocation.findings.invalidInput')}
                                </p>
                              ) : null}
                            </div>
                          ),
                        )}
                        {(['costCenterId', 'costObjectId'] as const).map((field) => (
                          <div key={field}>
                            <Label htmlFor={`${prefix}-${field}-${index}`}>
                              {t(`ledger.vendorAllocation.${field}`, { number: index + 1 })}
                            </Label>
                            <select
                              id={`${prefix}-${field}-${index}`}
                              {...form.register(`splits.${index}.${field}`)}
                              className="mt-1 h-11 w-full rounded-md border border-input bg-background px-2 text-sm"
                            >
                              <option value="">{t('common.none')}</option>
                              {review.availableDimensions
                                .filter(
                                  (dimension) =>
                                    dimension.kind ===
                                    (field === 'costCenterId' ? 'CENTER' : 'OBJECT'),
                                )
                                .map((dimension) => (
                                  <option key={dimension.id} value={dimension.id}>
                                    {dimension.code} · {dimension.name}
                                  </option>
                                ))}
                            </select>
                          </div>
                        ))}
                        <div className="sm:col-span-2">
                          <Label htmlFor={`${prefix}-description-${index}`}>
                            {t('ledger.vendorAllocation.description', { number: index + 1 })}
                          </Label>
                          <Input
                            id={`${prefix}-description-${index}`}
                            {...form.register(`splits.${index}.description`)}
                            className="mt-1 min-h-11"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={pending || rows.fields.length <= 1}
                            onClick={() => {
                              rows.remove(index);
                              if (form.getValues('selfAssessment'))
                                form.setValue('selfAssessment', null, { shouldDirty: true });
                              form.setValue(
                                'splits',
                                form
                                  .getValues('splits')
                                  .map((split, position) => ({ ...split, position: position + 1 })),
                                { shouldDirty: true },
                              );
                            }}
                          >
                            {t('ledger.vendorAllocation.remove', { number: index + 1 })}
                          </Button>
                        </div>
                        {local?.rows[index] ? (
                          <p className="text-sm text-muted-foreground sm:col-span-3">
                            {t('ledger.vendorAllocation.rowResult', {
                              deductible: formatLedgerMoney(
                                local.rows[index].deductibleTaxAmount,
                                review.currency,
                                i18n.language,
                              ),
                              cost: formatLedgerMoney(
                                local.rows[index].costAmount,
                                review.currency,
                                i18n.language,
                              ),
                            })}
                          </p>
                        ) : null}
                      </fieldset>
                    ))}
                    {local && !local.eligible ? (
                      <p className="text-sm text-destructive">
                        {t(
                          `ledger.vendorAllocation.findings.${local.findings[0] ?? 'invalidInput'}`,
                        )}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={pending || rows.fields.length >= 200}
                        onClick={() =>
                          rows.append({
                            position: rows.fields.length + 1,
                            accountId: '',
                            netAmount: '0',
                            taxRate: review.sourceBuckets[0]?.taxRate ?? '0',
                            inputTaxDeductionPercent: '100',
                            costCenterId: '',
                            costObjectId: '',
                            description: '',
                          })
                        }
                      >
                        {t('ledger.vendorAllocation.add')}
                      </Button>
                      <Button type="submit" disabled={pending || stale}>
                        {t('ledger.vendorAllocation.save')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    {review.lines.length ? (
                      <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border text-left">
                              <th className="p-2" scope="col">
                                {t('ledger.account')}
                              </th>
                              <th className="p-2 text-right" scope="col">
                                {t('ledger.debit')}
                              </th>
                              <th className="p-2 text-right" scope="col">
                                {t('ledger.credit')}
                              </th>
                              <th className="p-2" scope="col">
                                {t('ledger.vendorAllocation.dimensions')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {review.lines.map((line, index) => {
                              const account = review.accounts.find(
                                (entry) => entry.id === line.accountId,
                              );
                              return (
                                <tr key={index} className="border-b border-border last:border-0">
                                  <td className="p-2">
                                    {account
                                      ? `${account.number} · ${account.name}`
                                      : t('ledger.errors.accountMapping')}
                                  </td>
                                  <td className="p-2 text-right tabular-nums">
                                    {formatLedgerMoney(
                                      line.debit,
                                      review.baseCurrency,
                                      i18n.language,
                                    )}
                                  </td>
                                  <td className="p-2 text-right tabular-nums">
                                    {formatLedgerMoney(
                                      line.credit,
                                      review.baseCurrency,
                                      i18n.language,
                                    )}
                                  </td>
                                  <td className="p-2">
                                    {[line.costCenterId, line.costObjectId]
                                      .map(
                                        (id) =>
                                          review.dimensions.find((entry) => entry.id === id)?.name,
                                      )
                                      .filter(Boolean)
                                      .join(' · ') || '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {editable ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditing(true);
                          setChecked(false);
                        }}
                      >
                        {t('ledger.vendorAllocation.edit')}
                      </Button>
                    ) : null}
                  </>
                )}
                {mayApprove && !editing ? (
                  <div className="space-y-3 border-t border-border pt-3">
                    <div>
                      <Label htmlFor={`${prefix}-reason`}>
                        {t('ledger.vendorAllocation.reason')}
                      </Label>
                      <Input
                        id={`${prefix}-reason`}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        maxLength={500}
                        className="mt-1 min-h-11"
                      />
                    </div>
                    <Label
                      className="flex min-h-11 items-center gap-2"
                      htmlFor={`${prefix}-checked`}
                    >
                      <input
                        id={`${prefix}-checked`}
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => setChecked(event.target.checked)}
                      />
                      {t('ledger.vendorAllocation.checked')}
                    </Label>
                    <Button
                      type="button"
                      disabled={
                        !checked || !review.eligible || review.matchingRequired || stale || pending
                      }
                      onClick={() => void confirm()}
                    >
                      {t('ledger.vendorAllocation.approve')}
                    </Button>
                  </div>
                ) : null}
                <footer className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending}
                    onClick={() => void refresh()}
                  >
                    {t('ledger.vendorAllocation.refresh')}
                  </Button>
                  <Button type="button" variant="outline" disabled={pending} onClick={close}>
                    {t('common.cancel')}
                  </Button>
                </footer>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </FinancialFormProtection>
  );
}
